import { Group, Scene, Vector3, Euler } from 'three';
import { IEnemy } from './IEnemy';
import { AircraftPhysics, PhysicsConfig } from '../aircraft/AircraftPhysics';
import { buildGen4Model, GEN4_ENEMY_COLORS } from '../aircraft/Gen4Fighter';

type AIState = 'patrol' | 'chase' | 'attack' | 'evade';

/** AIパターン: pattern1=通常上空飛行, pattern2=低空飛行あり（ST4以降用） */
export type EliteAIPattern = 'pattern1' | 'pattern2';

// Pattern1: 最低巡航高度（これ以下には自発的に降りない）
const P1_MIN_CRUISE_ALT = 1000;
const P1_PREFERRED_ALT  = 1500; // パトロール時の目標高度

const ELITE_PHYSICS: PhysicsConfig = {
  maxSpeed: 800,
  minSpeed: 180,
  boostMultiplier: 1.2,
  acceleration: 180,
  deceleration: 140,
  pitchRate: 1.5,
  rollRate: 2.0,
  yawCoupling: 0.4,
  mouseSensitivity: 1.0,
  autoLevelStrength: 0.3,
};

// ── デフォルトAI定数 ──
const DETECT_RANGE    = 4000;
const ATTACK_RANGE    = 1800;
const MISSILE_RANGE   = 2500;
const GUN_COOLDOWN    = 3.0;
const MISSILE_COOLDOWN = 12.0;
const EVADE_DURATION  = 2.5;
const EVADE_HP_THRESHOLD = 0.3; // HP30%以下で離脱

// ── アグレッシブAI定数 ──
const AGG_DETECT_RANGE    = 7000;
const AGG_ATTACK_RANGE    = 2500;
const AGG_MISSILE_RANGE   = 3500;
const AGG_GUN_COOLDOWN    = 2.0;
const AGG_MISSILE_COOLDOWN = 8.0;
const AGG_LOCK_TIME       = 0.8;

/**
 * 精鋭戦闘機 — プレイヤーと同じ AircraftPhysics を使用
 *
 * EF-4 の赤色バリアント。プレイヤーと同等速度でドッグファイト可能。
 * 将来的にプレイヤー機体としても使用可能な設計。
 */
export class EliteFighter implements IEnemy {
  readonly group: Group;
  readonly physics: AircraftPhysics;

  private hp: number;
  private maxHp: number;
  private state: AIState = 'patrol';
  private spawnPos: Vector3;
  private patrolPoints: Vector3[];
  private patrolIdx = 0;
  private gunTimer = 0;
  private missileTimer = 0;
  private evadeTimer = 0;
  private evadeDir = 1; // ±1 回避方向
  private aggressive: boolean;
  private aiPattern: EliteAIPattern;

  // AI定数（aggressive切替で変わる）
  private aiDetectRange: number;
  private aiAttackRange: number;
  private aiMissileRange: number;
  private aiGunCooldown: number;
  private aiMissileCooldown: number;
  private aiLockTime: number;

  isAlive = true;
  hitRadius = 14;
  readonly isGroundUnit = false;

  /** ロックオン状態: プレイヤーをロックオン中か */
  lockingPlayer = false;
  lockTimer = 0;

  /** 弾丸・ミサイル発射コールバック (EnemyManagerが設定) */
  onFireBullet: ((from: Vector3, dir: Vector3) => void) | null = null;
  onFireMissile: ((from: Vector3, dir: Vector3) => void) | null = null;

  constructor(
    private scene: Scene,
    pos: [number, number, number],
    aggressive = false,
    aiPattern: EliteAIPattern = 'pattern1',
  ) {
    this.aggressive = aggressive;
    this.aiPattern = aiPattern;
    this.aiDetectRange    = aggressive ? AGG_DETECT_RANGE    : DETECT_RANGE;
    this.aiAttackRange    = aggressive ? AGG_ATTACK_RANGE    : ATTACK_RANGE;
    this.aiMissileRange   = aggressive ? AGG_MISSILE_RANGE   : MISSILE_RANGE;
    this.aiGunCooldown    = aggressive ? AGG_GUN_COOLDOWN    : GUN_COOLDOWN;
    this.aiMissileCooldown = aggressive ? AGG_MISSILE_COOLDOWN : MISSILE_COOLDOWN;
    this.aiLockTime       = aggressive ? AGG_LOCK_TIME       : 1.2;

    this.maxHp = 80;
    this.hp = 80;
    this.physics = new AircraftPhysics(ELITE_PHYSICS);
    this.physics.speed = 250; // 初期速度（パトロール向き低速）

    this.group = buildGen4Model(GEN4_ENEMY_COLORS);
    this.group.position.set(...pos);
    scene.add(this.group);

    const [x, y, z] = pos;
    this.spawnPos = new Vector3(x, y, z);
    const BOUND = 9000;
    const clampX = (v: number) => Math.max(-BOUND, Math.min(BOUND, v));
    const clampZ = (v: number) => Math.max(-BOUND, Math.min(BOUND, v));
    const clampY = (v: number) => Math.max(200, Math.min(5000, v));
    this.patrolPoints = [
      new Vector3(clampX(x + 600),  clampY(y),        clampZ(z)),
      new Vector3(clampX(x + 600),  clampY(y + 100),  clampZ(z + 600)),
      new Vector3(clampX(x),        clampY(y),        clampZ(z + 600)),
      new Vector3(clampX(x - 300),  clampY(y - 50),   clampZ(z)),
    ];
  }

  get position(): Vector3 { return this.group.position; }

  takeDamage(amount: number): boolean {
    this.hp -= amount;
    if (this.hp <= 0) {
      this.hp = 0;
      this.isAlive = false;
      this.scene.remove(this.group);
    }
    return !this.isAlive;
  }

  /** @returns プレイヤーへのダメージ量 */
  update(delta: number, playerPos: Vector3): number {
    if (!this.isAlive) return 0;

    const dist = this.position.distanceTo(playerPos);
    this.gunTimer = Math.max(0, this.gunTimer - delta);
    this.missileTimer = Math.max(0, this.missileTimer - delta);

    // ── 状態遷移 ──
    if (this.state === 'evade') {
      this.evadeTimer -= delta;
      if (this.evadeTimer <= 0) {
        this.state = dist < this.aiAttackRange ? 'attack' : 'chase';
      }
    } else if (this.hp / this.maxHp < EVADE_HP_THRESHOLD && dist < this.aiAttackRange) {
      this.state = 'evade';
      this.evadeTimer = EVADE_DURATION;
      this.evadeDir = Math.random() > 0.5 ? 1 : -1;
    } else if (dist < this.aiAttackRange) {
      this.state = 'attack';
    } else if (dist < this.aiDetectRange) {
      this.state = 'chase';
    } else {
      this.state = 'patrol';
    }

    // ── 速度更新（AircraftPhysicsの速度管理のみ使用） ──
    const aiInput = this.computeAIInput(delta, playerPos, dist);
    this.physics.update(delta, 0, 0, aiInput.accel, aiInput.decel, aiInput.boost);

    // ── AI直接操舵（ロール→ヨーではなく、直接ヨー・ピッチを目標に向ける） ──
    const toTarget = aiInput.targetPos.clone().sub(this.position);
    const flatDist = Math.sqrt(toTarget.x * toTarget.x + toTarget.z * toTarget.z);

    // 目標ヨー角
    const targetYaw = Math.atan2(-toTarget.x, -toTarget.z);
    // 目標ピッチ角
    const targetPitch = flatDist > 10 ? -Math.atan2(toTarget.y, flatDist) : 0;

    // ヨー差分を -π〜π に正規化
    let yawDiff = targetYaw - this.physics.yaw;
    if (yawDiff > Math.PI) yawDiff -= Math.PI * 2;
    if (yawDiff < -Math.PI) yawDiff += Math.PI * 2;

    // 旋回速度（状態で変わる）
    const turnSpeed = this.state === 'patrol' ? 1.5 : 2.5;
    this.physics.yaw += Math.max(-turnSpeed * delta, Math.min(turnSpeed * delta, yawDiff));
    // ヨー正規化
    if (this.physics.yaw > Math.PI) this.physics.yaw -= Math.PI * 2;
    if (this.physics.yaw < -Math.PI) this.physics.yaw += Math.PI * 2;

    // ピッチ
    const pitchDiff = targetPitch - this.physics.pitch;
    this.physics.pitch += Math.max(-1.5 * delta, Math.min(1.5 * delta, pitchDiff));

    // ロールは旋回方向に傾ける（見た目用）
    const targetRoll = -yawDiff * 0.8;
    this.physics.roll += (targetRoll - this.physics.roll) * 3.0 * delta;

    // ── 位置更新 ──
    const vel = this.physics.getVelocity();
    this.group.position.addScaledVector(vel, delta);

    // 地面・天井・境界
    const floorAlt = this.aiPattern === 'pattern1' ? P1_MIN_CRUISE_ALT : 80;
    if (this.group.position.y < floorAlt) {
      this.group.position.y = floorAlt;
      if (this.physics.pitch > 0) this.physics.pitch *= 0.5;
    }
    if (this.group.position.y > 2000) {
      this.group.position.y = 2000;
      if (this.physics.pitch < 0) this.physics.pitch = 0;
    }
    const BOUND = 5000;
    this.group.position.x = Math.max(-BOUND, Math.min(BOUND, this.group.position.x));
    this.group.position.z = Math.max(-BOUND, Math.min(BOUND, this.group.position.z));

    this.group.rotation.set(this.physics.pitch, this.physics.yaw, this.physics.roll, 'YXZ');

    // ── ロックオン更新 ──
    this.updateLockOn(delta, playerPos, dist);

    // ── 攻撃 ──
    return this.handleAttack(delta, playerPos, dist);
  }

  private computeAIInput(
    _delta: number,
    playerPos: Vector3,
    dist: number,
  ): { targetPos: Vector3; accel: boolean; decel: boolean; boost: boolean } {
    let targetPos: Vector3;
    let accel = false;
    let decel = false;
    let boost = false;

    // Pattern1用: プレイヤー座標の高度を最低巡航高度以上に補正
    const isP1 = this.aiPattern === 'pattern1';
    const adjustedPlayerPos = playerPos.clone();
    if (isP1) {
      adjustedPlayerPos.y = Math.max(adjustedPlayerPos.y, P1_MIN_CRUISE_ALT);
    }

    switch (this.state) {
      case 'patrol': {
        // プレイヤーに向かってゆっくり接近（上空を維持）
        targetPos = adjustedPlayerPos.clone();
        if (isP1) {
          targetPos.y = Math.max(targetPos.y, P1_PREFERRED_ALT);
        }
        if (this.physics.speed > 300) decel = true;
        break;
      }
      case 'chase':
        targetPos = adjustedPlayerPos.clone();
        accel = true;
        break;
      case 'attack':
        targetPos = adjustedPlayerPos.clone();
        if (dist < 500) decel = true;
        else accel = true;
        break;
      case 'evade': {
        // 横方向に逃げつつ距離を取る
        const awayDir = this.position.clone().sub(playerPos).normalize();
        const lateral = new Vector3(-awayDir.z, 0, awayDir.x).multiplyScalar(this.evadeDir);
        targetPos = this.position.clone()
          .add(awayDir.multiplyScalar(800))
          .add(lateral.multiplyScalar(600));
        const evadeMinAlt = isP1 ? P1_MIN_CRUISE_ALT : 200;
        targetPos.y = Math.min(2000, Math.max(evadeMinAlt, this.position.y));
        boost = true;
        break;
      }
      default:
        targetPos = adjustedPlayerPos.clone();
    }

    return { targetPos, accel, decel, boost };
  }

  private updateLockOn(delta: number, playerPos: Vector3, dist: number): void {
    // 近距離（1200m以内）または射程外ではロック解除
    const MISSILE_MIN_DIST = 1200;
    if (dist < MISSILE_MIN_DIST || dist > this.aiMissileRange || this.state === 'patrol') {
      this.lockingPlayer = false;
      this.lockTimer = Math.max(0, this.lockTimer - delta * 2);
      return;
    }
    // evade中はロック維持だが新規ロックはしない
    if (this.state === 'evade' && !this.lockingPlayer) {
      this.lockTimer = Math.max(0, this.lockTimer - delta);
      return;
    }

    // 前方角度チェック（aggressiveは90度、通常は60度）
    const forward = new Vector3(0, 0, -1).applyEuler(
      new Euler(this.physics.pitch, this.physics.yaw, this.physics.roll, 'YXZ')
    );
    const toPlayer = playerPos.clone().sub(this.position).normalize();
    const angleDot = forward.dot(toPlayer);
    const lockAngle = this.aggressive ? 0.0 : 0.5; // cos(90°)=0, cos(60°)=0.5

    if (angleDot > lockAngle) {
      this.lockTimer = Math.min(this.lockTimer + delta, this.aiLockTime);
    } else {
      this.lockTimer = Math.max(0, this.lockTimer - delta * 2);
    }

    this.lockingPlayer = this.lockTimer >= this.aiLockTime;
  }

  private handleAttack(_delta: number, playerPos: Vector3, dist: number): number {
    if (this.state !== 'attack' && this.state !== 'chase') return 0;

    const fwd = new Vector3(0, 0, -1).applyEuler(
      new Euler(this.physics.pitch, this.physics.yaw, this.physics.roll, 'YXZ')
    );

    // 近距離（1200m以内）はバルカンのみ、中距離以遠でミサイル
    if (this.lockingPlayer && this.missileTimer <= 0 && this.onFireMissile && dist > 1200) {
      this.missileTimer = this.aiMissileCooldown;
      const from = this.position.clone().addScaledVector(fwd, 12);
      this.onFireMissile(from, fwd.clone());
    }

    // 機銃発射（attack のみ、近距離）
    if (this.state === 'attack' && dist < 1200 && this.gunTimer <= 0 && this.onFireBullet) {
      this.gunTimer = this.aiGunCooldown;
      const from = this.position.clone().addScaledVector(fwd, 12);
      this.onFireBullet(from, fwd.clone());
    }

    return 0;
  }
}
